import { useEffect, useRef } from 'react';
import {
  DocumentReference,
  DocumentSnapshot,
  Query,
  QuerySnapshot,
  onSnapshot,
} from 'firebase/firestore';

export type FirestoreTriggerSnapshot = DocumentSnapshot | QuerySnapshot | null;

type UseFirestoreTriggerOptions = {
  /** Whether the subscription should be active at all. */
  enabled: boolean;
  /**
   * The document or query to watch. Must be referentially stable (memoize with
   * useMemo) — a new object every render tears down and recreates the
   * listener. Pass null while `enabled` to skip Firestore entirely and rely on
   * the polling fallback (e.g. when viewing data the signed-in user doesn't
   * own and security rules would reject the listener).
   */
  target: DocumentReference | Query | null;
  /**
   * Called with the snapshot on every change (including the initial snapshot
   * right after subscribing), or with null on each polling-fallback tick.
   * Handlers should treat this purely as a change signal and re-derive state
   * idempotently.
   */
  onTrigger: (snapshot: FirestoreTriggerSnapshot) => void;
  /** Cadence of the fallback poll when no listener is active. */
  fallbackIntervalMs?: number;
};

/**
 * Fires `onTrigger` whenever the watched Firestore document or query changes,
 * replacing the fixed-interval REST polling loops used previously. If the
 * listener cannot be established (offline, or security rules not yet deployed
 * to the environment) it degrades to interval-based triggering so the UI
 * keeps updating exactly as it did with polling.
 *
 * IMPORTANT — the first trigger has no delay. The polling loops this replaced
 * waited out a full interval before their first tick, which gave the backend
 * time to write the in-flight status the handler reads. `onSnapshot` delivers
 * a snapshot as soon as it subscribes, so a handler can now observe the state
 * from *before* the work it is waiting on was ever recorded. "No in-flight
 * status" therefore means "hasn't started yet" just as often as it means
 * "already finished", and a handler that reads it as the latter will tear down
 * its own loading state — and, if `enabled` depends on that state, unsubscribe
 * before the real update ever arrives, stranding the UI until a page refresh.
 *
 * Every call site avoids this by keeping to one of two rules:
 *
 * 1. Derive `enabled` from server-confirmed state (a status or count that came
 *    back from a fetch, or an id the request returned), never from state set at
 *    click time. Then there is no window in which the status can be missing.
 * 2. If `enabled` must come from client state set optimistically, only flip it
 *    on after the request resolves, and make sure the endpoint writes the
 *    in-flight status *before* it responds. The handler must additionally treat
 *    a terminal/absent status as "not started" until it has actually observed
 *    the work in flight — see the recipe substitution listener in MealPlanView,
 *    which tracks that explicitly and keeps a watchdog for work that never
 *    shows up.
 *
 * Reordering an endpoint to enqueue before it writes status, or moving a
 * listener's enable to before its `await`, reintroduces the race.
 */
export const useFirestoreTrigger = ({
  enabled,
  target,
  onTrigger,
  fallbackIntervalMs = 3000,
}: UseFirestoreTriggerOptions) => {
  // Keep the latest callback in a ref so a new closure each render doesn't
  // resubscribe the listener.
  const onTriggerRef = useRef(onTrigger);
  onTriggerRef.current = onTrigger;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let unsubscribe: (() => void) | null = null;

    const startFallbackPolling = () => {
      if (cancelled || intervalId) return;
      intervalId = setInterval(() => onTriggerRef.current(null), fallbackIntervalMs);
    };

    if (target) {
      const handleError = (error: Error) => {
        console.error('Firestore listener failed, falling back to polling:', error);
        if (unsubscribe) {
          unsubscribe();
          unsubscribe = null;
        }
        startFallbackPolling();
      };

      if (target instanceof DocumentReference) {
        unsubscribe = onSnapshot(
          target,
          (snapshot) => onTriggerRef.current(snapshot),
          handleError
        );
      } else {
        unsubscribe = onSnapshot(
          target,
          (snapshot) => onTriggerRef.current(snapshot),
          handleError
        );
      }
    } else {
      startFallbackPolling();
    }

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      if (intervalId) clearInterval(intervalId);
    };
  }, [enabled, target, fallbackIntervalMs]);
};
